import { ExtendedShareholderDTO } from "@/types/Shareholder";
import { makeAutoObservable } from "mobx";
import { newUuid } from "@/utils/uuid";
import {
  DemergerShareTypeData,
  IssueNewSharesDTO,
  IssueShareTypeDTO,
  Transaction,
} from "@/types/Transactions";
import { compact, groupBy, identity, sortBy, sum, uniqBy } from "lodash";
import Big from "big.js";
import { IssuedBy } from "@/types/const/Transactions";
import {
  ShareTypeBonusIssueModel,
  SubscriberForBonusIssueModel,
} from "@/dbot/capitalIncreaseThroughBonusIssue/capitalIncreaseThroughBonusIssueDomain";
import { combineDateAndTime } from "@/utils/Dates";
import { TMessage } from "@/i18n/tt";
import {
  BooleanFieldModel,
  LocalDateFieldModel,
  NumberFieldModel,
  RadioFieldModel,
} from "@/common/commonForms";
import { getShareSumByType } from "@/shareholderRegister/shareholderRegisterDomain";
import { ShareholderIdShares } from "@/types/excelImportDomain";
import { findShareTypeNameMessage, ShareType, ShareTypeCode, shareTypeLabel } from "@/common/share";
import {
  excludedShareholdersFromActors,
  excludeExistingShareholders,
} from "@/stores/ShareholdersStore";
import { TransactionDateCardModel } from "@/shareholderRegister/transactions/commonTransactionsDomain";
import { TransactionType } from "@/types/enums/TransactionType";
import { Language } from "@/i18n/languages";
import { TMessageKey } from "@/i18n/types.gen";
import { TransactionStore } from "@/stores/TransactionStore";

export class IssueOfNewSharesTransactionModel {
  isRegistered: BooleanFieldModel;
  registrationDate: TransactionDateCardModel;
  shareholderRightsDate: TransactionDateCardModel;
  issuedBy: IssuedByModel;
  isBonusIssue: BooleanFieldModel;
  increaseType: RadioFieldModel<IncreaseType>;
  shareIssue: ShareIssueModel;
  isDirty = false;
  shareNominalValue: NumberFieldModel;
  optionExerciseIds?: string[];

  constructor(
    readonly shareholderData: {
      activeShareTypes: ShareType[];
      shareholders: ExtendedShareholderDTO[];
      shareSumBySharetypeCode: { [shareTypeCode in ShareTypeCode]?: number };
      transactionLog: Transaction[];
      loadShareholdersForDate: (orgNumber: string, date: string) => Generator;
    },
    readonly companyData: {
      orgNumber: string;
      currency: string;
    },
    readonly transactionStore: TransactionStore,
    shareholderIdShares?: ShareholderIdShares[],
    transactionData?: IssueNewSharesDTO,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.isRegistered = new BooleanFieldModel({
      value: transactionData ? !!transactionData.registrationDate : true,
    });
    this.registrationDate = new TransactionDateCardModel({
      data: shareholderData,
      value: transactionData?.registrationDate,
      onChange: (date, time) => {
        this.setDirty();
        this.updateShareholderData(date, time);
      },
    });
    this.shareholderRightsDate = new TransactionDateCardModel({
      data: shareholderData,
      value: transactionData?.shareholderRightsDate,
      onChange: (date, time) => {
        this.setDirty();
        this.updateShareholderData(date, time);
      },
    });
    const issuedByDecisionDate = (
      transactionData?.boardMeetingPoDDate ?? transactionData?.generalMeetingDate
    )?.split("T")[0];
    this.issuedBy = new IssuedByModel(
      this.setDirty,
      transactionData?.issuedBy,
      issuedByDecisionDate,
    );
    this.shareIssue = new ShareIssueModel(
      shareholderData,
      companyData,
      transactionData?.shareNominalValue,
      this.setDirty,
      shareholderIdShares,
      transactionData?.shareAllocation,
      transactionData?.isBonusIssue,
      transactionData?.demergerData?.demergerShareTypeData,
    );
    this.isBonusIssue = new BooleanFieldModel({
      value: transactionData?.isBonusIssue,
      onChange: () => {
        this.setDirty();
        this.issuedBy.update();
      },
    });
    this.increaseType = createIncreaseTypeModel(
      transactionData?.increaseType ?? IncreaseType.newIssue,
      this.setDirty,
    );
    this.shareNominalValue = new NumberFieldModel({
      onChange: this.setDirty,
      value: transactionData?.shareNominalValue,
      isPositive: true,
    });
    this.optionExerciseIds = transactionData?.optionExerciseIds;
  }

  get oldNominalValue() {
    return this.transactionStore.shareNominalValue;
  }

  get hasNoExistingShares() {
    const shareSums = getShareSumByType(this.shareholderData.shareholders);
    return Object.values(shareSums).every((sum) => sum === 0);
  }

  get hasDate() {
    return (
      this.registrationDate.combinedDateAndTime !== undefined ||
      this.shareholderRightsDate.combinedDateAndTime !== undefined
    );
  }

  setDirty() {
    this.isDirty = true;
  }

  clearDirty() {
    this.isDirty = false;
  }

  get error(): TMessage | undefined {
    return this.shareIssue.error;
  }

  *updateShareholderData(date?: string, time?: string) {
    if (date && time) {
      const isoDate = combineDateAndTime(date, time);
      yield this.shareholderData.loadShareholdersForDate(this.companyData.orgNumber, isoDate);
      yield this.transactionStore.fetchInitialTransactionState(this.companyData.orgNumber, isoDate);
      this.shareIssue.setShareNominalValue(this.transactionStore.shareNominalValue);
    }
  }

  validate(): boolean {
    this.registrationDate.validate();
    this.issuedBy.validate();
    this.shareIssue.validate();

    const hasDecisionDate = !!this.issuedBy.decisionDate.validatedValue;
    if (!this.isRegistered.value) {
      if (hasDecisionDate) {
        this.shareholderRightsDate.clearErrors();
      } else {
        this.shareholderRightsDate.validate();
      }
    }

    return (
      (this.isRegistered.value
        ? this.registrationDate.isValid
        : this.shareholderRightsDate.isValid || hasDecisionDate) &&
      this.issuedBy.isValid &&
      this.shareIssue.isValid
    );
  }

  prepareData(): IssueNewSharesDTO {
    return {
      organizationnumber: this.companyData.orgNumber,
      transactionType: TransactionType.ISSUE,
      registrationDate: this.isRegistered.value
        ? this.registrationDate.combinedDateAndTime
        : undefined,
      shareholderRightsDate: this.isRegistered.value
        ? undefined
        : this.shareholderRightsDate.combinedDateAndTime,
      shareAllocation: this.shareIssue.prepareData(),
      isBonusIssue: this.isBonusIssue.value,
      increaseType: this.increaseType.value,
      registeredInNorwegian: this.isRegistered.value ?? false,
      isPending: !this.isRegistered.value,
      shareNominalValue: this.hasNoExistingShares ? this.shareNominalValue.parsedValue : undefined,
      optionExerciseIds: this.optionExerciseIds,
      ...this.issuedBy.prepareData(),
    };
  }
}

export function createCurrencyModel(
  defaultCurrency: string,
  setDirty: () => void,
): RadioFieldModel<string> {
  return new RadioFieldModel<string>({
    value: defaultCurrency,
    options: [defaultCurrency],
    idFn: identity,
    labelFn: (c) => ({ translated: c }),
    onChange: setDirty,
  });
}

export enum IncreaseType {
  debtConversion = "debtConversion",
  newIssue = "newIssue",
}

export enum IncorporationType {
  newIssue = "newIssue",
  demerger = "demerger",
}

export const incorporationTypeLabels: Record<IncorporationType, TMessageKey> = {
  [IncorporationType.newIssue]: "transactions.incorporation.type-ordinary-incorporation",
  [IncorporationType.demerger]: "transactions.incorporation.type-demerger",
};

export function createIncreaseTypeModel(
  increaseType: IncreaseType,
  setDirty: () => void,
): RadioFieldModel<IncreaseType> {
  return new RadioFieldModel<IncreaseType>({
    value: increaseType,
    options: Object.values(IncreaseType),
    idFn: identity,
    labelFn: (t) => ({ key: `transactions.issueTransaction.increaseType.${t}` }),
    onChange: setDirty,
  });
}

export class ShareTypeIssueModel {
  readonly shareType: ShareType;
  shareNominalValue: number;
  numberOfShares: NumberFieldModel;
  price: NumberFieldModel;
  currency: string;
  transferorShareTypeCode: RadioFieldModel<ShareTypeCode>;
  transferorNumberOfShares: NumberFieldModel;
  transferorShareCapital: NumberFieldModel;
  transferorSharePremium: NumberFieldModel;
  isDemerger: boolean;

  constructor(
    shareType: ShareType,
    currentCurrency: string,
    setDirty: () => void,
    shareNominalValue: number,
    numberOfShares?: number,
    price?: number,
    isBonusIssue?: boolean,
    demergerData?: DemergerShareTypeData,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.shareType = shareType;
    this.shareNominalValue = shareNominalValue;
    this.numberOfShares = new NumberFieldModel({
      onChange: setDirty,
      isInteger: true,
      isPositiveOrZero: true,
      value: numberOfShares,
    });
    this.price = new NumberFieldModel({
      onChange: setDirty,
      validator: (value) => {
        return value !== undefined && value < this.shareNominalValue
          ? {
              key: "transactions.issueTransaction.share-price.greater-than-nominal",
              params: { currency: currentCurrency, nominalValue: this.shareNominalValue },
            }
          : undefined;
      },
      required: numberOfShares !== undefined && isBonusIssue !== true,
      isPositiveOrZero: true,
      value: price,
    });
    this.currency = currentCurrency;

    const shareTypeCodes: Array<ShareTypeCode> = Object.values(ShareTypeCode).filter(
      (s) => s !== ShareTypeCode.UNKNOWN,
    );
    this.transferorShareTypeCode = new RadioFieldModel({
      value: demergerData?.transferorShareTypeCode ?? shareTypeCodes[0],
      options: shareTypeCodes,
      labelFn: (shareTypeCode) => shareTypeLabel(shareTypeCode),
      idFn: (shareTypeCode) => shareTypeCode,
    });
    this.transferorNumberOfShares = new NumberFieldModel({
      onChange: setDirty,
      isInteger: true,
      required: true,
      value: demergerData?.transferorNumberOfShares,
    });
    this.transferorShareCapital = new NumberFieldModel({
      onChange: setDirty,
      required: true,
      value: demergerData?.transferorShareCapital,
    });
    this.transferorSharePremium = new NumberFieldModel({
      onChange: setDirty,
      required: true,
      value: demergerData?.transferorSharePremium,
    });
    this.isDemerger = demergerData !== undefined;
  }

  setShareNominalValue(nominalValue: number) {
    this.shareNominalValue = nominalValue;
  }

  setDemerger(isDemerger: boolean) {
    this.isDemerger = isDemerger;
  }

  isPriceRequired(isBonusIssue?: boolean) {
    this.price.setRequired(
      !this.numberOfShares.isEmpty && isBonusIssue !== true && !this.isDemerger,
    );
  }

  get isEmpty(): boolean {
    return this.numberOfShares.isEmpty;
  }

  get isValid(): boolean {
    if (!this.numberOfShares.isValid) {
      return false;
    }
    return this.isDemerger ? this.isTransferorDataValid : this.price.isValid;
  }

  private get isTransferorDataValid(): boolean {
    return (
      this.isEmpty ||
      (this.transferorNumberOfShares.isValid &&
        this.transferorShareCapital.isValid &&
        this.transferorSharePremium.isValid)
    );
  }

  validate(): boolean {
    this.numberOfShares.validate();

    if (!this.isDemerger) {
      this.price.validate();
    } else if (this.numberOfShares.parsedValue) {
      this.transferorNumberOfShares.validate();
      this.transferorShareCapital.validate();
      this.transferorSharePremium.validate();
    }
    return this.isValid;
  }
}

export class SubscriberModel {
  id = newUuid();
  shareType: ShareType;
  shareNominalValue: number;
  shareholder: ExtendedShareholderDTO;
  numberOfShares: NumberFieldModel;
  price: NumberFieldModel;
  sharePremium: NumberFieldModel;
  paidInShareCapital: NumberFieldModel;
  bonusShareCapital: NumberFieldModel;
  isEmployeeShare?: boolean;
  currency;

  constructor(
    shareType: ShareType,
    shareholder: ExtendedShareholderDTO,
    currentCurrency: string,
    setDirty: () => void,
    shareNominalValue: number,
    numberOfShares?: number,
    price?: number,
    prefill?: {
      sharePremium?: number;
      paidInShareCapital?: number;
      bonusShareCapital?: number;
      isEmployeeShare?: boolean;
    },
  ) {
    this.shareType = shareType;
    this.isEmployeeShare = prefill?.isEmployeeShare;
    this.shareNominalValue = shareNominalValue;
    this.shareholder = shareholder;
    this.numberOfShares = new NumberFieldModel({
      onChange: setDirty,
      value: numberOfShares,
      isPositiveOrZero: true,
    });
    this.price = new NumberFieldModel({
      onChange: setDirty,
      isPositiveOrZero: true,
      value: price,
      validator: (value) => {
        return value !== undefined && value < this.shareNominalValue
          ? {
              key: "transactions.issueTransaction.share-price.greater-than-nominal",
              params: { currency: currentCurrency, nominalValue: this.shareNominalValue },
            }
          : undefined;
      },
    });
    this.sharePremium = new NumberFieldModel({
      onChange: setDirty,
      isPositiveOrZero: true,
      value: prefill?.sharePremium,
    });
    this.paidInShareCapital = new NumberFieldModel({
      onChange: setDirty,
      isPositiveOrZero: true,
      value: prefill?.paidInShareCapital,
    });
    this.bonusShareCapital = new NumberFieldModel({
      onChange: setDirty,
      isPositiveOrZero: true,
      value: prefill?.bonusShareCapital,
    });
    this.currency = createCurrencyModel(currentCurrency, setDirty);
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setShareNominalValue(nominalValue: number) {
    this.shareNominalValue = nominalValue;
  }

  get isEmpty(): boolean {
    return this.numberOfShares.isEmpty;
  }

  isValid(isDeviatingPrice?: boolean): boolean {
    return this.numberOfShares.isValid && (isDeviatingPrice ? this.price.isValid : true);
  }

  validate(isDeviatingPrice?: boolean): boolean {
    this.numberOfShares.validate();
    this.price.validate();
    return this.isValid(isDeviatingPrice);
  }

  prepareData(
    isDeviatingPrice: boolean,
    shareIssueModels: ShareTypeIssueModel[],
    isDemerger?: boolean,
  ): IssueShareTypeDTO | null {
    const shareIssue = shareIssueModels.find(
      (shareIssueModel) => shareIssueModel.shareType.sharetypeId === this.shareType.sharetypeId,
    );
    const numberOfShares = this.numberOfShares.toNumberOrZero();
    if (!shareIssue || shareIssue.isEmpty || !numberOfShares) {
      return null;
    }

    return {
      sharetypeId: this.shareType.sharetypeId,
      shareholderId: this.shareholder.id,
      numberOfShares: numberOfShares,
      sharePrice: isDeviatingPrice
        ? (this.price.parsedValue ?? shareIssue.price.toNumberOrZero())
        : shareIssue.price.toNumberOrZero(),
      currency: isDeviatingPrice ? this.currency.value : shareIssue.currency,
      sharePremium: isDemerger ? this.sharePremium.toNumber() : undefined,
      paidInShareCapital: isDemerger ? this.paidInShareCapital.toNumber() : undefined,
      bonusShareCapital: isDemerger ? this.bonusShareCapital.toNumber() : undefined,
      isEmployeeShare: this.isEmployeeShare,
    };
  }
}

function initIssuedByRadioModel(
  setDirty: () => void,
  isBonusIssue?: boolean,
  initialIssuedBy?: IssuedBy,
): RadioFieldModel<IssuedBy> {
  return new RadioFieldModel<IssuedBy>({
    value: isBonusIssue ? IssuedBy.GENERAL_MEETING : (initialIssuedBy ?? IssuedBy.BOARD_MEETING),
    options: isBonusIssue ? [IssuedBy.GENERAL_MEETING] : Object.values(IssuedBy),
    idFn: identity,
    labelFn: (v) => {
      switch (v) {
        case IssuedBy.BOARD_MEETING:
          return { key: "transactions.issueTransaction.pOa" };
        case IssuedBy.GENERAL_MEETING:
          return { key: "transactions.issueTransaction.generalMeeting" };
      }
    },
    onChange: setDirty,
  });
}

export class IssuedByModel {
  issuedBy: RadioFieldModel<IssuedBy>;
  setDirty: () => void;
  decisionDate: LocalDateFieldModel;

  constructor(setDirty: () => void, initialIssuedBy?: IssuedBy, initialDecisionDate?: string) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.issuedBy = initIssuedByRadioModel(setDirty, undefined, initialIssuedBy);
    this.decisionDate = new LocalDateFieldModel({ onChange: setDirty, value: initialDecisionDate });
    this.setDirty = setDirty;
  }

  get isValid(): boolean {
    return this.decisionDate.isValid;
  }

  update(isBonusIssue?: boolean) {
    this.issuedBy = initIssuedByRadioModel(this.setDirty, isBonusIssue);
  }

  validate(): boolean {
    this.decisionDate.validate();
    return this.isValid;
  }

  prepareData(): {
    issuedBy?: IssuedBy;
    boardMeetingPoDDate?: string | null;
    generalMeetingDate?: string | null;
  } {
    switch (this.issuedBy.value) {
      case IssuedBy.GENERAL_MEETING:
        return {
          issuedBy: this.issuedBy.value,
          generalMeetingDate: this.decisionDate.validatedValue
            ? combineDateAndTime(this.decisionDate.validatedValue, "12:00")
            : undefined,
        };
      case IssuedBy.BOARD_MEETING:
        return {
          issuedBy: this.issuedBy.value,
          boardMeetingPoDDate: this.decisionDate.validatedValue
            ? combineDateAndTime(this.decisionDate.validatedValue, "12:00")
            : undefined,
        };
      default:
        return {};
    }
  }
}

export type SummaryTableRow = {
  id: string;
  name: TMessage;
  sharesBefore: number;
  sharesAfter: number;
  price: number;
  newShareCapital: number;
  shareCapitalBefore: number;
  noChange: boolean;
};

export function getShareSummaryTableData(
  {
    shareTypeIssues,
    shareNominalValue,
    shareholders,
  }: {
    shareTypeIssues: ShareTypeIssueModel[];
    shareNominalValue: number;
    shareholders: ExtendedShareholderDTO[];
  },
  lang: Language,
): SummaryTableRow[] {
  const shareSumByType = getShareSumByType(shareholders);
  return shareTypeIssues.map((shareIssue) => {
    const newShares = shareIssue.numberOfShares.toNumberOrZero();
    const sharesBefore = shareSumByType[shareIssue.shareType.shareTypeCode] ?? 0;
    const sharesAfter = sharesBefore + newShares;
    return {
      id: `sharedata-${shareIssue.shareType.sharetypeId}`,
      name: findShareTypeNameMessage(shareIssue.shareType, lang),
      sharesBefore,
      sharesAfter,
      price: shareNominalValue,
      shareCapitalBefore: Big(shareNominalValue).mul(sharesBefore).toNumber(),
      newShareCapital: Big(shareNominalValue).mul(sharesAfter).toNumber(),
      noChange: newShares === 0,
    };
  });
}

type AllocationStatus = { shareType: ShareType; remaining: number; total: number };

export function getAllocationStatus(
  shareTypeIssues: ShareTypeIssueModel[] | ShareTypeBonusIssueModel[],
  subscribers: (SubscriberModel | SubscriberForBonusIssueModel)[],
): AllocationStatus[] {
  return shareTypeIssues
    .filter((shareIssue) => !shareIssue.isEmpty)
    .map((shareIssue) => {
      const total = shareIssue.numberOfShares.toNumberOrZero();
      return {
        shareType: shareIssue.shareType,
        total,
        remaining:
          total -
          subscribers.reduce((sum, subscriber) => {
            if (subscriber.shareType.sharetypeId === shareIssue.shareType.sharetypeId) {
              return sum + subscriber.numberOfShares.toNumberOrZero();
            }
            return sum;
          }, 0),
      };
    });
}

function initSubscribers(
  shareTypes: ShareType[],
  shareholders: ExtendedShareholderDTO[],
  currency: string,
  setDirty: () => void,
  shareNominalValue: number,
  shareholderIdShares?: ShareholderIdShares[],
  shareAllocation?: IssueShareTypeDTO[],
  prefillPrice?: boolean,
): SubscriberModel[] {
  return sortBy(shareholders, "name").flatMap((shareholder) => {
    const shareholderData = shareholderIdShares?.find((s) => s.shareholderId === shareholder.id);
    return shareTypes.map((shareType) => {
      const shareData = shareholderData?.shares?.find(
        (s) => s.shareTypeId === shareType.sharetypeId,
      );
      const alloc = shareAllocation?.find(
        (a) => a.shareholderId === shareholder.id && a.sharetypeId === shareType.sharetypeId,
      );
      const numberOfShares = shareData?.numberOfShares ?? alloc?.numberOfShares;
      const price =
        prefillPrice && alloc?.sharePrice && alloc.sharePrice > 0 ? alloc.sharePrice : undefined;
      return new SubscriberModel(
        shareType,
        shareholder,
        currency,
        setDirty,
        shareNominalValue,
        numberOfShares,
        price,
        {
          sharePremium: alloc?.sharePremium,
          paidInShareCapital: alloc?.paidInShareCapital,
          bonusShareCapital: alloc?.bonusShareCapital,
          isEmployeeShare: alloc?.isEmployeeShare,
        },
      );
    });
  });
}

function createShareTypeIssueModel(
  shareType: ShareType,
  currency: string,
  setDirty: () => void,
  shareNominalValue: number,
  shareholderIdShares?: ShareholderIdShares[],
  allocations?: IssueShareTypeDTO[],
  isBonusIssue?: boolean,
  demergerData?: DemergerShareTypeData,
) {
  const shares = shareholderIdShares
    ?.flatMap((s) => s.shares ?? [])
    .filter((s) => s.shareTypeId === shareType.sharetypeId);
  const numberOfSharesFromShIds = shares ? sum(shares.map((s) => s.numberOfShares)) : undefined;
  const numberOfSharesFromAllocs =
    allocations && allocations.length > 0
      ? sum(allocations.map((a) => a.numberOfShares))
      : undefined;
  const numberOfShares = numberOfSharesFromShIds ?? numberOfSharesFromAllocs;

  let price: number | undefined;
  if (allocations && allocations.length > 0 && !isBonusIssue) {
    const prices = allocations.map((a) => a.sharePrice);
    const allSamePrice = prices.every((p) => p === prices[0]);
    if (allSamePrice && prices[0] > 0) {
      price = prices[0];
    } else if (!allSamePrice) {
      const minPositivePrice = Math.min(...prices.filter((p) => p > 0));
      if (isFinite(minPositivePrice)) {
        price = minPositivePrice;
      }
    }
  }

  return new ShareTypeIssueModel(
    shareType,
    currency,
    setDirty,
    shareNominalValue,
    numberOfShares,
    price,
    isBonusIssue,
    demergerData,
  );
}

function computeIsDeviatingPrice(shareAllocation?: IssueShareTypeDTO[]): boolean {
  if (!shareAllocation || shareAllocation.length === 0) return false;
  const allocationsByShareType = groupBy(shareAllocation, (a) => a.sharetypeId);
  return Object.values(allocationsByShareType).some((allocs) => {
    if (allocs.length === 0) return false;
    const prices = allocs.map((a) => a.sharePrice);
    return !prices.every((p) => p === prices[0]);
  });
}

export class ShareIssueModel {
  shareNominalValue: number;
  shareTypeIssues: ShareTypeIssueModel[];
  subscribers: SubscriberModel[];
  isDeviatingPrice: BooleanFieldModel;
  error?: TMessage;
  allocationErrorMessages: TMessage[] = [];
  isDemerger: boolean;

  constructor(
    readonly shareholderData: {
      activeShareTypes: ShareType[];
      shareholders: ExtendedShareholderDTO[];
    },
    readonly companyData: {
      orgNumber: string;
      currency: string;
    },
    shareNominalValue: number | undefined,
    private setDirty: () => void,
    private shareholderIdShares?: ShareholderIdShares[],
    shareAllocation?: IssueShareTypeDTO[],
    isBonusIssue?: boolean,
    demergerShareTypeData?: DemergerShareTypeData[],
  ) {
    this.shareNominalValue = shareNominalValue ?? 0;
    this.isDemerger = demergerShareTypeData !== undefined;
    const isDeviating = !isBonusIssue && computeIsDeviatingPrice(shareAllocation);
    this.isDeviatingPrice = new BooleanFieldModel({ value: isDeviating || undefined });

    const allocationsByShareType = shareAllocation
      ? groupBy(shareAllocation, (a) => a.sharetypeId)
      : {};

    this.shareTypeIssues = this.shareTypes.map((shareType) => {
      const allocations = allocationsByShareType[shareType.sharetypeId];
      const demergerData = demergerShareTypeData?.find(
        (d) => d.shareTypeId === shareType.sharetypeId,
      );
      return createShareTypeIssueModel(
        shareType,
        this.currency,
        setDirty,
        this.shareNominalValue,
        shareholderIdShares,
        allocations,
        isBonusIssue,
        demergerData,
      );
    });
    this.shareTypeIssues.forEach((s) => s.setDemerger(this.isDemerger));

    this.subscribers = initSubscribers(
      this.shareTypes,
      this.shareholders,
      this.currency,
      setDirty,
      this.shareNominalValue,
      shareholderIdShares,
      shareAllocation,
      isDeviating || demergerShareTypeData !== undefined,
    );

    makeAutoObservable(this);
  }

  get shareTypes() {
    return this.shareholderData.activeShareTypes;
  }

  get shareholders() {
    return this.shareholderData.shareholders;
  }

  get currency() {
    return this.companyData.currency;
  }

  onSharetypeAdded(shareType: ShareType) {
    const shareTypeIssue = createShareTypeIssueModel(
      shareType,
      this.currency,
      this.setDirty,
      this.shareNominalValue,
      this.shareholderIdShares,
    );
    shareTypeIssue.setDemerger(this.isDemerger);
    this.shareTypeIssues.push(shareTypeIssue);
    const subscribers = this.subscribers;
    this.subscribers = uniqBy(
      subscribers.map((s) => s.shareholder),
      (shareholder) => shareholder.id,
    ).flatMap((shareholder) => [
      ...subscribers.filter((s) => s.shareholder.id === shareholder.id),
      new SubscriberModel(
        shareType,
        shareholder,
        this.currency,
        this.setDirty,
        this.shareNominalValue,
      ),
    ]);
  }

  addNewSubscriber(shareholder: ExtendedShareholderDTO) {
    this.subscribers = [
      ...this.subscribers,
      ...this.shareTypes.map(
        (shareType) =>
          new SubscriberModel(
            shareType,
            shareholder,
            this.currency,
            this.setDirty,
            this.shareNominalValue,
          ),
      ),
    ];
  }

  get activeShareTypeIds(): number[] {
    return this.shareTypeIssues
      .filter((s) => !s.numberOfShares.isEmpty)
      .map((s) => s.shareType.sharetypeId);
  }

  get isSubscribers(): boolean {
    return this.subscribers.length > 0;
  }

  get allocatedSubscriberShares(): { [key in ShareTypeCode]?: number } {
    const groupedSubscribers = groupBy(this.subscribers, (e) => e.shareType.shareTypeCode);
    const result: { [key in ShareTypeCode]?: number } = {};
    Object.entries(groupedSubscribers).forEach(([sharetypeCode, subscribers]) => {
      result[sharetypeCode as ShareTypeCode] = subscribers.reduce(
        (p, c) => p + c.numberOfShares.toNumberOrZero(),
        0,
      );
    });
    return result;
  }

  get isValid(): boolean {
    return (
      !this.error &&
      this.isDeviatingPrice.isValid &&
      this.allocationErrorMessages.length === 0 &&
      this.shareTypeIssues.every((s) => s.isValid) &&
      this.subscribers.every((s) => s.isValid()) &&
      getAllocationStatus(this.shareTypeIssues, this.subscribers).every((s) => s.remaining === 0)
    );
  }

  setShareNominalValue(nominalValue: number | undefined) {
    this.shareNominalValue = nominalValue ?? 0;
    this.shareTypeIssues.map((d) => d.setShareNominalValue(this.shareNominalValue));
    this.subscribers.map((d) => d.setShareNominalValue(this.shareNominalValue));
  }

  setDemerger(isDemerger: boolean) {
    this.isDemerger = isDemerger;
    this.shareTypeIssues.forEach((s) => s.setDemerger(isDemerger));
    if (isDemerger) {
      this.prefillSubscriberPrices();
    }
  }

  private prefillSubscriberPrices() {
    this.subscribers
      .filter((s) => !s.isEmpty && s.price.isEmpty)
      .forEach((subscriber) => {
        const price = this.shareTypeIssues.find(
          (s) => s.shareType.sharetypeId === subscriber.shareType.sharetypeId,
        )?.price.parsedValue;
        if (price !== undefined) {
          subscriber.price.setAndCommitInputValue(price);
        }
      });
  }

  validate(): boolean {
    this.allocationErrorMessages = this.getShareTypeAllocationErrors();
    this.isDeviatingPrice.validate();
    if (this.isDemerger && this.isMissingPricePerShareholder) {
      this.allocationErrorMessages.push({ key: "transactions.issueTransaction.noPriceError" });
    }
    this.shareTypeIssues.map((s) => s.validate());
    this.subscribers.map((s) => s.validate(this.isDeviatingPrice.value));

    this.error = this.shareTypeIssues.every((s) => s.isEmpty)
      ? { key: "transactions.issueTransaction.noShareIssueError" }
      : this.subscribers.every((s) => s.isEmpty)
        ? { key: "transactions.issueTransaction.noSubscribersError" }
        : getAllocationStatus(this.shareTypeIssues, this.subscribers).some((s) => s.remaining !== 0)
          ? { key: "transactions.issueTransaction.incompleteSubscribersError" }
          : undefined;
    return this.isValid;
  }

  private get isMissingPricePerShareholder(): boolean {
    return this.subscribers.some((s) => s.numberOfShares.parsedValue && !s.price.parsedValue);
  }

  private getShareTypeAllocationErrors(): TMessage[] {
    const allocatedShares = this.allocatedSubscriberShares;
    return this.shareTypeIssues
      .filter((share) => share.numberOfShares.toNumberOrZero() > 0)
      .filter(
        (s) => s.numberOfShares.toNumberOrZero() !== allocatedShares[s.shareType.shareTypeCode],
      )
      .map((value) => ({
        key: "transactions.issueTransaction.number-of-shares.error",
        params: { n: { type: "shareType", value: value.shareType } },
      }));
  }

  prepareData(): IssueShareTypeDTO[] {
    return compact(
      this.subscribers.map((m) =>
        m.prepareData(
          (this.isDeviatingPrice.value || this.isDemerger) ?? false,
          this.shareTypeIssues,
          this.isDemerger,
        ),
      ),
    );
  }

  get excludedShareholders() {
    return excludedShareholdersFromActors(
      this.subscribers.map((subscriber) => subscriber.shareholder),
    );
  }

  addNewSubscribers(shareholders: ExtendedShareholderDTO[], onSuccess: () => void) {
    excludeExistingShareholders(
      shareholders,
      this.subscribers.map((subscriber) => subscriber.shareholder.id),
    ).forEach((shareholder) => this.addNewSubscriber(shareholder));
    onSuccess();
  }

  prepareDemergerData(): DemergerShareTypeData[] {
    return this.shareTypeIssues
      .filter((issue) => issue.numberOfShares.parsedValue)
      .map((issue) => ({
        shareTypeId: issue.shareType.sharetypeId,
        transferorShareTypeCode: issue.transferorShareTypeCode.value,
        transferorNumberOfShares: issue.transferorNumberOfShares.parsedValue!,
        transferorShareCapital: issue.transferorShareCapital.parsedValue!,
        transferorSharePremium: issue.transferorSharePremium.parsedValue!,
      }));
  }
}
